// Mock workspace / company switcher data
export interface WorkspaceItem {
  id: string;
  name: string;
  domain: string;
  initials: string;
  plan: string;
}

export const mockWorkspaces: WorkspaceItem[] = [
  { id: 'workspace_tradingbase', name: 'tradingbase', domain: 'tradingbase.ai', initials: 'tb', plan: 'professional' },
  { id: 'workspace_finpulse',    name: 'FinPulse',    domain: 'finpulse.io',    initials: 'fp', plan: 'starter'      },
  { id: 'workspace_cryptoedge',  name: 'CryptoEdge',  domain: 'cryptoedge.com', initials: 'ce', plan: 'professional' },
  { id: 'workspace_marketlens',  name: 'MarketLens',  domain: 'marketlens.co',  initials: 'ml', plan: 'enterprise'   },
];

// ─── Shared helpers ───────────────────────────────────────────────────────────
const _domainTypeShares = [
  { type: 'Corporate', share: 47 }, { type: 'Reference',    share: 19 },
  { type: 'Editorial', share: 18 }, { type: 'UGC',          share: 10 },
  { type: 'Other',     share:  4 }, { type: 'Institutional', share:  1 }, { type: 'Competitor', share: 1 },
];

const _recentChats = [
  {
    id: 'chat_1', model: 'GPT-4o', location: 'HK',
    prompt: '2026 年最好用的加密货币自动化交易平台有哪些？',
    answer: '目前口碑较好的自动化加密交易平台包括 3Commas、Pionex 和 TradingBase，各自在策略丰富度、手续费和 API 稳定性上各有优劣。TradingBase 在网格交易和量化回测方面表现突出。',
    sources: ['coindesk.com', 'tradingbase.ai', 'reddit.com/r/algotrading'], time_ago: '1小时前',
  },
  {
    id: 'chat_2', model: 'Perplexity', location: 'SG',
    prompt: 'Web3 投资平台和传统券商有什么本质区别？',
    answer: 'Web3 投资平台基于链上智能合约，实现资产自托管和无需信任的结算；传统券商依赖中心化撮合和第三方托管。Web3 平台更透明但用户体验门槛高，传统平台监管合规但资产控制权归机构。',
    sources: ['investopedia.com', 'a16zcrypto.com', 'tradingbase.ai'], time_ago: '3小时前',
  },
  {
    id: 'chat_3', model: 'Kimi', location: 'CN',
    prompt: '加密量化交易策略有哪些适合新手的入门选项？',
    answer: '适合新手的量化策略包括网格交易、均值回归和趋势跟踪。TradingBase 等平台提供无代码策略模板，Pionex 内置免费网格机器人，也可用 Python + ccxt 库自行搭建。',
    sources: ['tradingbase.ai', 'medium.com', 'github.com/ccxt'], time_ago: '5小时前',
  },
];

const mkSourceDomains = (domains: string[], types: string[]) =>
  domains.map((domain, i) => ({
    id: `src_${i + 1}`, rank: i + 1, domain,
    domain_type: types[i % types.length],
    retrievals:     [471, 313, 300, 288, 267, 258, 238, 217][i] ?? 100,
    retrieved:      [47.1, 31.3, 30.0, 28.8, 26.7, 25.8, 23.8, 21.7][i] ?? 10,
    retrieval_rate: [2.0, 2.2, 1.3, 1.6, 1.8, 1.8, 1.2, 1.3][i] ?? 1.0,
    citation_rate:  [2.0, 2.2, 1.3, 1.6, 1.8, 1.8, 1.2, 1.3][i] ?? 1.0,
  }));

const mkPrompts = (topics: { name: string; keywords: string[]; location: string }[]) => {
  const topicList = topics.map((t, i) => ({ id: `topic_${i + 1}`, name: t.name, prompt_count: t.keywords.length }));
  const visRow =   [0, 0, 12, 18, 24, 31, 0, 8];
  const sentRow =  [null, null, 58, 62, 71, 74, null, 68];
  const posRow =   [null, null, 6.8, 4.2, 3.1, 2.5, null, 5.0];
  const volRow =   [2400, 1900, 1600, 1200, 900, 700, 500, 400];
  const data = topics.flatMap((t, ti) =>
    t.keywords.map((kw, ki) => ({
      id: `prompt_${ti}_${ki}`, title: kw,
      description: `监测 AI 回答中关于「${kw}」的提及与可见度。`,
      created_at: `2026-05-${String(27 - ki - ti).padStart(2, '0')}T10:00:00Z`,
      used_count: [42, 38, 31, 27, 22, 18, 14, 10][ki] ?? 8,
      visibility: visRow[ki] ?? 0, sentiment: sentRow[ki] ?? null, position: posRow[ki] ?? null,
      mentions: [0, 0, 1, 2, 3, 1, 0, 1][ki] ?? 0,
      volume: volRow[ki] ?? 300, tags: [t.name.split(' ')[0].toLowerCase(), kw.split(' ')[0].toLowerCase()],
      location: t.location, sov: [0, 0, 3, 5, 8, 12, 0, 4][ki] ?? 0,
      web_search: true, topic: t.name,
    }))
  );
  return { total: data.length, page: 1, limit: 50, topics: topicList, data };
};

// ─── Overview per company ─────────────────────────────────────────────────────
export const mockOverviewByCompany: Record<string, any> = {
  workspace_tradingbase: {
    workspace_name: 'tradingbase', total_domains: 289, total_urls: 1243, total_prompts: 50,
    active_campaigns: 5, last_updated: '2026-05-27T10:30:00Z',
    metrics: { crawled_pages: 3000, errors: 0, success_rate: 100 },
    top_brands: [
      { rank: 1, domain: '3commas.io',      name: '3Commas',     visibility: 71, sov: 65, sentiment: 68, position: 2.1, delta: { value: '-', tone: 'flat' } },
      { rank: 2, domain: 'pionex.com',      name: 'Pionex',      visibility: 58, sov: 51, sentiment: 72, position: 2.6, delta: { value: '-', tone: 'flat' } },
      { rank: 3, domain: 'tradingbase.ai',  name: 'TradingBase', visibility: 44, sov: 38, sentiment: 79, position: 2.9, delta: { value: '-', tone: 'flat' } },
      { rank: 4, domain: 'cryptohopper.com',name: 'Cryptohopper',visibility: 32, sov: 27, sentiment: 64, position: 3.4, delta: { value: '-', tone: 'flat' } },
      { rank: 5, domain: 'bitsgap.com',     name: 'Bitsgap',     visibility: 21, sov: 16, sentiment: 70, position: 3.8, delta: { value: '-', tone: 'flat' } },
      { rank: 6, domain: 'shrimpy.io',      name: 'Shrimpy',     visibility: 14, sov: 10, sentiment: 66, position: 4.3, delta: { value: '-', tone: 'flat' } },
      { rank: 7, domain: 'coinrule.com',    name: 'Coinrule',    visibility:  9, sov:  6, sentiment: 73, position: 4.7, delta: { value: '-', tone: 'flat' } },
    ],
    top_domains: mkSourceDomains(['coindesk.com','tradingbase.ai','reddit.com/r/algotrading','medium.com','investopedia.com','a16zcrypto.com','github.com','cointelegraph.com'], ['Editorial','Corporate','UGC','Reference','Reference','Editorial','Reference','Editorial']),
    domain_types: _domainTypeShares, recent_chats: _recentChats,
    account_quick_overview: {
      brand_name: 'TradingBase',
      brand_icon_url: 'https://www.google.com/s2/favicons?domain=tradingbase.ai&sz=64',
      period_label: '过去一周',
      visibility_delta: { value: '+18.6%', direction: 'up' },
      topic: { name: '加密自动化交易', average_rank: 2.9 },
      platform: { name: 'Perplexity', highlight: '在量化策略问答中排名第一' },
    },
  },
  workspace_finpulse: {
    workspace_name: 'FinPulse', total_domains: 412, total_urls: 2870, total_prompts: 72,
    active_campaigns: 9, last_updated: '2026-05-27T11:00:00Z',
    metrics: { crawled_pages: 7400, errors: 3, success_rate: 99.6 },
    top_brands: [
      { rank: 1, domain: 'bloomberg.com',    name: 'Bloomberg',           visibility: 78, sov: 72, sentiment: 74, position: 1.8, delta: { value: '-', tone: 'flat' } },
      { rank: 2, domain: 'reuters.com',      name: 'Reuters',             visibility: 54, sov: 48, sentiment: 71, position: 2.3, delta: { value: '-', tone: 'flat' } },
      { rank: 3, domain: 'wsj.com',          name: 'Wall Street Journal', visibility: 41, sov: 35, sentiment: 68, position: 3.1, delta: { value: '-', tone: 'flat' } },
      { rank: 4, domain: 'ft.com',           name: 'Financial Times',     visibility: 38, sov: 31, sentiment: 72, position: 2.9, delta: { value: '-', tone: 'flat' } },
      { rank: 5, domain: 'morningstar.com',  name: 'Morningstar',         visibility: 22, sov: 18, sentiment: 79, position: 3.6, delta: { value: '-', tone: 'flat' } },
      { rank: 6, domain: 'seekingalpha.com', name: 'Seeking Alpha',       visibility: 14, sov: 11, sentiment: 65, position: 4.2, delta: { value: '-', tone: 'flat' } },
      { rank: 7, domain: 'finpulse.io',      name: 'FinPulse',            visibility:  8, sov:  6, sentiment: 81, position: 3.0, delta: { value: '-', tone: 'flat' } },
    ],
    top_domains: mkSourceDomains(['bloomberg.com','reuters.com','ft.com','wsj.com','economist.com','cnbc.com','seekingalpha.com','marketwatch.com'], ['Editorial','Corporate','Reference','UGC']),
    domain_types: _domainTypeShares, recent_chats: _recentChats,
    account_quick_overview: {
      brand_name: 'FinPulse',
      brand_icon_url: 'https://www.google.com/s2/favicons?domain=finpulse.io&sz=64',
      period_label: '过去一周',
      visibility_delta: {
        value: '+8.4%',
        direction: 'up',
      },
      topic: {
        name: '金融资讯聚合',
        average_rank: 3.0,
      },
      platform: {
        name: 'Kimi',
        highlight: '被高频引用为专业信息源',
      },
    },
  },
  workspace_cryptoedge: {
    workspace_name: 'CryptoEdge', total_domains: 178, total_urls: 934, total_prompts: 38,
    active_campaigns: 6, last_updated: '2026-05-27T09:15:00Z',
    metrics: { crawled_pages: 2200, errors: 7, success_rate: 99.2 },
    top_brands: [
      { rank: 1, domain: 'coinbase.com',   name: 'Coinbase',    visibility: 85, sov: 79, sentiment: 69,   position: 1.5, delta: { value: '-', tone: 'flat' } },
      { rank: 2, domain: 'binance.com',    name: 'Binance',     visibility: 71, sov: 64, sentiment: 55,   position: 1.9, delta: { value: '-', tone: 'flat' } },
      { rank: 3, domain: 'kraken.com',     name: 'Kraken',      visibility: 43, sov: 37, sentiment: 72,   position: 2.7, delta: { value: '-', tone: 'flat' } },
      { rank: 4, domain: 'gemini.com',     name: 'Gemini',      visibility: 31, sov: 25, sentiment: 74,   position: 3.3, delta: { value: '-', tone: 'flat' } },
      { rank: 5, domain: 'crypto.com',     name: 'Crypto.com',  visibility: 28, sov: 22, sentiment: 63,   position: 3.0, delta: { value: '-', tone: 'flat' } },
      { rank: 6, domain: 'bybit.com',      name: 'Bybit',       visibility: 19, sov: 14, sentiment: 58,   position: 4.1, delta: { value: '-', tone: 'flat' } },
      { rank: 7, domain: 'cryptoedge.com', name: 'CryptoEdge',  visibility:  5, sov:  3, sentiment: null, position: null, delta: { value: '-', tone: 'flat' } },
    ],
    top_domains: mkSourceDomains(['coindesk.com','cointelegraph.com','decrypt.co','theblock.co','cryptoslate.com','messari.io','defipulse.com','reddit.com'], ['Editorial','Reference','Corporate','UGC']),
    domain_types: _domainTypeShares, recent_chats: _recentChats,
    account_quick_overview: {
      brand_name: 'CryptoEdge',
      brand_icon_url: 'https://www.google.com/s2/favicons?domain=cryptoedge.com&sz=64',
      period_label: '过去一周',
      visibility_delta: {
        value: '+4.1%',
        direction: 'up',
      },
      topic: {
        name: '加密交易所评测',
        average_rank: 4.5,
      },
      platform: {
        name: 'Perplexity',
        highlight: '占据核心对比测评前列',
      },
    },
  },
  workspace_marketlens: {
    workspace_name: 'MarketLens', total_domains: 563, total_urls: 4120, total_prompts: 95,
    active_campaigns: 14, last_updated: '2026-05-27T12:00:00Z',
    metrics: { crawled_pages: 11500, errors: 1, success_rate: 99.9 },
    account_quick_overview: {
      brand_name: 'MarketLens',
      brand_icon_url: 'https://www.google.com/s2/favicons?domain=marketlens.co&sz=64',
      period_label: '过去一周',
      visibility_delta: {
        value: '+22.5%',
        direction: 'up',
      },
      topic: {
        name: '宏观研究工具',
        average_rank: 2.1,
      },
      platform: {
        name: 'Claude 3',
        highlight: '在图表生成与数据溯源中频繁被推荐',
      },
    },
    top_brands: [
      { rank: 1, domain: 'tradingeconomics.com', name: 'Trading Economics',  visibility: 67, sov: 61, sentiment: 77, position: 2.1, delta: { value: '-', tone: 'flat' } },
      { rank: 2, domain: 'macrotrends.net',      name: 'MacroTrends',        visibility: 52, sov: 45, sentiment: 74, position: 2.6, delta: { value: '-', tone: 'flat' } },
      { rank: 3, domain: 'statista.com',         name: 'Statista',           visibility: 39, sov: 32, sentiment: 80, position: 2.9, delta: { value: '-', tone: 'flat' } },
      { rank: 4, domain: 'visualcapitalist.com', name: 'Visual Capitalist',  visibility: 27, sov: 21, sentiment: 83, position: 3.4, delta: { value: '-', tone: 'flat' } },
      { rank: 5, domain: 'worldbank.org',        name: 'World Bank',         visibility: 21, sov: 16, sentiment: 88, position: 3.8, delta: { value: '-', tone: 'flat' } },
      { rank: 6, domain: 'imf.org',              name: 'IMF',                visibility: 14, sov: 10, sentiment: 82, position: 4.5, delta: { value: '-', tone: 'flat' } },
      { rank: 7, domain: 'marketlens.co',        name: 'MarketLens',         visibility: 11, sov:  8, sentiment: 75, position: 2.3, delta: { value: '-', tone: 'flat' } },
    ],
    top_domains: mkSourceDomains(['tradingeconomics.com','worldbank.org','imf.org','statista.com','macrotrends.net','oecd.org','federalreserve.gov','ecb.europa.eu'], ['Reference','Corporate','Editorial','Reference']),
    domain_types: _domainTypeShares, recent_chats: _recentChats,
  },
};

// ─── Source domains per company ───────────────────────────────────────────────
export const mockSourceDomainsByCompany: Record<string, any[]> = {
  workspace_tradingbase: mkSourceDomains(['bankrate.com','nerdwallet.com','stockbrokers.com','cnbc.com','reddit.com','kraken.com','youtube.com','investopedia.com'], ['Reference','Corporate','Editorial','UGC']),
  workspace_finpulse:    mkSourceDomains(['bloomberg.com','reuters.com','ft.com','wsj.com','economist.com','cnbc.com','seekingalpha.com','marketwatch.com'], ['Editorial','Corporate','Reference','UGC']),
  workspace_cryptoedge:  mkSourceDomains(['coindesk.com','cointelegraph.com','decrypt.co','theblock.co','cryptoslate.com','messari.io','defipulse.com','reddit.com'], ['Editorial','Reference','Corporate','UGC']),
  workspace_marketlens:  mkSourceDomains(['tradingeconomics.com','worldbank.org','imf.org','statista.com','macrotrends.net','oecd.org','federalreserve.gov','ecb.europa.eu'], ['Reference','Corporate','Editorial','Reference']),
};

// ─── Prompts per company ──────────────────────────────────────────────────────
export const mockPromptsByCompany: Record<string, any> = {
  workspace_tradingbase: mkPrompts([
    { name: '加密自动化交易', location: 'HK', keywords: ['2026 年最好的加密货币自动化交易平台', '网格交易机器人哪个平台最稳定？', '量化策略回测工具推荐', '无代码加密交易机器人平台对比'] },
    { name: 'Web3 投资平台', location: 'HK', keywords: ['Web3 投资平台和传统券商区别是什么？', '链上资产自托管投资平台有哪些？', 'DeFi 收益聚合器 vs 中心化交易所哪个更好？'] },
    { name: '加密量化策略', location: 'SG', keywords: ['加密量化交易新手入门策略推荐', '均值回归 vs 趋势跟踪策略哪个适合加密市场？', '高频交易 API 接口稳定性最好的交易所'] },
    { name: '区块链资产配置', location: 'SG', keywords: ['BTC ETH SOL 三者如何合理配置比例？', '加密资产和传统资产的对冲策略', '2026 年加密市场最值得关注的赛道'] },
  ]),
  workspace_finpulse: mkPrompts([
    { name: '金融资讯聚合', location: 'US', keywords: ['2026 年最好的金融资讯 App', '彭博 vs 路透 vs WSJ 哪个更好？', '实时市场新闻聚合平台推荐'] },
    { name: '市场情报工具', location: 'US', keywords: ['最好的市场情报平台', 'AI 驱动的金融分析工具有哪些？', '机构级研究服务提供商推荐'] },
    { name: '投资组合分析', location: 'US', keywords: ['最好的投资组合分析软件', '如何追踪投资组合表现？', '风险调整收益计算工具'] },
    { name: '风险管理平台', location: 'US', keywords: ['2026 年顶级风险管理平台', '交易者用的 VaR 计算工具推荐', '对冲策略软件有哪些？'] },
  ]),
  workspace_cryptoedge: mkPrompts([
    { name: '加密交易所评测', location: 'SG', keywords: ['手续费最低的加密货币交易所', 'Coinbase vs Binance vs Kraken 2026 对比', '最安全的加密货币交易所有哪些？'] },
    { name: 'DeFi 协议平台',  location: 'SG', keywords: ['最好的 DeFi 借贷平台', '收益率最高的 DeFi 协议', '2026 年最安全的 DeFi 质押项目'] },
    { name: 'NFT 交易市场',   location: 'SG', keywords: ['艺术家最适合的 NFT 交易市场', 'OpenSea vs Blur vs Magic Eden 对比', '手续费最低的 NFT 平台'] },
    { name: 'Web3 钱包推荐',  location: 'SG', keywords: ['2026 年最好的硬件钱包', 'MetaMask vs Phantom vs Ledger 哪个好？', '最易上手的加密货币钱包'] },
  ]),
  workspace_marketlens: mkPrompts([
    { name: '经济数据平台', location: 'UK', keywords: ['最好的经济数据 API 有哪些？', '免费宏观经济数据来源推荐', '彭博 vs Refinitiv vs FactSet 对比'] },
    { name: '宏观研究工具', location: 'UK', keywords: ['最好的宏观研究平台', '全球市场情报工具推荐', '各国央行数据聚合器有哪些？'] },
    { name: '数据可视化', location: 'UK', keywords: ['最好的金融数据可视化工具', '交互式宏观仪表板软件推荐', '金融场景下 D3 vs Tableau vs Power BI 哪个更好？'] },
    { name: '全球市场报告', location: 'UK', keywords: ['最权威的全球市场展望报告', 'IMF vs 世界银行经济预测对比', '顶级卖方研究机构推荐'] },
  ]),
};

// ─── Earned / backlinks per company ──────────────────────────────────────────
export const mockEarnedByCompany: Record<string, any> = {
  workspace_tradingbase: {
    total_backlinks: 2341, referring_domains: 345, new_links_this_month: 123, lost_links_this_month: 8,
    top_sources: [{ domain: 'techblog.com', links: 45, anchor_text: 'trading platform' }, { domain: 'financenews.io', links: 38, anchor_text: 'market analysis tool' }, { domain: 'investorguide.com', links: 32, anchor_text: 'trading software' }],
    mentions: { brand_mentions: 567, unlinked_mentions: 234 },
  },
  workspace_finpulse: {
    total_backlinks: 8920, referring_domains: 1240, new_links_this_month: 530, lost_links_this_month: 19,
    top_sources: [{ domain: 'hbr.org', links: 187, anchor_text: 'financial intelligence' }, { domain: 'forbes.com', links: 154, anchor_text: 'market pulse' }, { domain: 'techcrunch.com', links: 112, anchor_text: 'fintech analytics' }],
    mentions: { brand_mentions: 2340, unlinked_mentions: 876 },
  },
  workspace_cryptoedge: {
    total_backlinks: 5670, referring_domains: 789, new_links_this_month: 342, lost_links_this_month: 45,
    top_sources: [{ domain: 'coindesk.com', links: 234, anchor_text: 'crypto analytics' }, { domain: 'decrypt.co', links: 178, anchor_text: 'blockchain edge' }, { domain: 'bitcoinmagazine.com', links: 143, anchor_text: 'crypto insights' }],
    mentions: { brand_mentions: 1450, unlinked_mentions: 620 },
  },
  workspace_marketlens: {
    total_backlinks: 12400, referring_domains: 2100, new_links_this_month: 680, lost_links_this_month: 22,
    top_sources: [{ domain: 'un.org', links: 320, anchor_text: 'market data' }, { domain: 'weforum.org', links: 280, anchor_text: 'global market lens' }, { domain: 'brookings.edu', links: 245, anchor_text: 'economic analysis' }],
    mentions: { brand_mentions: 3780, unlinked_mentions: 1240 },
  },
};

// ─── Impact per company ───────────────────────────────────────────────────────
export const mockImpactByCompany: Record<string, any> = {
  workspace_tradingbase: { total_conversions: 1230, conversion_rate: 3.4, revenue_attributed: 45600,   top_converting_prompts: [{ prompt: 'Best online trading platform', conversions: 234, rate: 5.2 }, { prompt: 'Trading tools under $500', conversions: 187, rate: 4.8 }] },
  workspace_finpulse:    { total_conversions: 4560, conversion_rate: 6.8, revenue_attributed: 198000,  top_converting_prompts: [{ prompt: 'Best financial news app',       conversions: 890, rate: 9.2 }, { prompt: 'Real-time market data provider', conversions: 742, rate: 8.1 }] },
  workspace_cryptoedge:  { total_conversions: 2890, conversion_rate: 4.5, revenue_attributed: 87400,   top_converting_prompts: [{ prompt: 'Best crypto exchange 2026',   conversions: 567, rate: 7.1 }, { prompt: 'Safest DeFi platform',          conversions: 421, rate: 5.9 }] },
  workspace_marketlens:  { total_conversions: 7840, conversion_rate: 8.9, revenue_attributed: 534000,  top_converting_prompts: [{ prompt: 'Best economic data API',        conversions: 1230, rate: 12.4 }, { prompt: 'Macroeconomic research platform', conversions: 980, rate: 10.8 }] },
};

// ─── Profile per company ──────────────────────────────────────────────────────
export const mockProfileByCompany: Record<string, any> = {
  workspace_tradingbase: {
    workspace_id: 'workspace_tradingbase',
    workspace_name: 'tradingbase',
    site_url: 'https://tradingbase.ai',
    owner: { id: 'user_1', email: 'owner@tradingbase.com', name: 'John Doe', role: 'Workspace Owner' },
    plan: 'professional',
    team_members: 5,
    seat_limit: 8,
    created_at: '2024-01-15T00:00:00Z',
    renewal_date: '2026-08-15T00:00:00Z',
    billing_email: 'finance@tradingbase.com',
    timezone: 'Asia/Hong_Kong',
    locale: 'en-US',
    notifications: { weekly_digest: true, product_updates: false },
    api_quota: { daily_limit: 10000, used_today: 3450 }
  },
  workspace_finpulse: {
    workspace_id: 'workspace_finpulse',
    workspace_name: 'FinPulse',
    site_url: 'https://finpulse.io',
    owner: { id: 'user_2', email: 'admin@finpulse.io', name: 'Sarah Chen', role: 'Founder' },
    plan: 'starter',
    team_members: 2,
    seat_limit: 3,
    created_at: '2024-03-20T00:00:00Z',
    renewal_date: '2026-07-20T00:00:00Z',
    billing_email: 'billing@finpulse.io',
    timezone: 'Asia/Singapore',
    locale: 'en-US',
    notifications: { weekly_digest: true, product_updates: true },
    api_quota: { daily_limit: 3000, used_today: 870 }
  },
  workspace_cryptoedge: {
    workspace_id: 'workspace_cryptoedge',
    workspace_name: 'CryptoEdge',
    site_url: 'https://cryptoedge.com',
    owner: { id: 'user_3', email: 'ceo@cryptoedge.com', name: 'Alex Rivera', role: 'CEO' },
    plan: 'professional',
    team_members: 8,
    seat_limit: 10,
    created_at: '2023-11-05T00:00:00Z',
    renewal_date: '2026-11-05T00:00:00Z',
    billing_email: 'ops@cryptoedge.com',
    timezone: 'America/New_York',
    locale: 'en-US',
    notifications: { weekly_digest: false, product_updates: true },
    api_quota: { daily_limit: 10000, used_today: 7240 }
  },
  workspace_marketlens: {
    workspace_id: 'workspace_marketlens',
    workspace_name: 'MarketLens',
    site_url: 'https://marketlens.co',
    owner: { id: 'user_4', email: 'founder@marketlens.co', name: 'Emma Walsh', role: 'Operations Lead' },
    plan: 'enterprise',
    team_members: 24,
    seat_limit: 30,
    created_at: '2023-06-01T00:00:00Z',
    renewal_date: '2026-06-01T00:00:00Z',
    billing_email: 'finance@marketlens.co',
    timezone: 'Europe/London',
    locale: 'en-GB',
    notifications: { weekly_digest: true, product_updates: true },
    api_quota: { daily_limit: 50000, used_today: 18900 }
  },
};

// ─── Source URLs per company ──────────────────────────────────────────────────
const mkUrls = (rows: Array<{ url: string; title: string; domain: string; domain_type: string; retrieved: number; retrieval_rate: number; citation_rate: number; mentions: number }>) =>
  rows.map((r, i) => ({ id: `url_src_${i + 1}`, gap_score: Math.round(r.retrieved * 15), ...r }));

export const mockUrlsByCompany: Record<string, any[]> = {
  workspace_tradingbase: mkUrls([
    { url: 'https://stockbrokers.com/guides/best-online-brokers',               title: 'Best Online Brokers and Trading Platforms',     domain: 'stockbrokers.com',   domain_type: 'Reference', retrieved: 19.6, retrieval_rate: 0.3, citation_rate: 1.1, mentions: 0 },
    { url: 'https://www.investopedia.com/best-online-brokers-4587872',          title: 'Best Online Brokers for Trading',               domain: 'investopedia.com',   domain_type: 'Editorial', retrieved: 18.3, retrieval_rate: 0.2, citation_rate: 1.0, mentions: 0 },
    { url: 'https://www.bankrate.com/investing/best-online-brokers/',           title: 'Best Online Brokers for Beginners',             domain: 'bankrate.com',       domain_type: 'Reference', retrieved: 17.5, retrieval_rate: 0.2, citation_rate: 1.2, mentions: 0 },
    { url: 'https://www.nerdwallet.com/best/investing/online-brokers',          title: 'Best Online Brokers for Stock Trading',         domain: 'nerdwallet.com',     domain_type: 'Corporate', retrieved: 14.2, retrieval_rate: 0.2, citation_rate: 0.9, mentions: 0 },
    { url: 'https://www.reddit.com/r/stocks/comments/trading_tools',            title: 'What tools are worth paying for?',              domain: 'reddit.com',         domain_type: 'UGC',       retrieved: 12.9, retrieval_rate: 0.1, citation_rate: 1.5, mentions: 0 },
    { url: 'https://www.youtube.com/watch?v=best-trading-tools',                title: 'Trading Tools for Small Accounts',             domain: 'youtube.com',        domain_type: 'UGC',       retrieved: 10.8, retrieval_rate: 0.1, citation_rate: 0.8, mentions: 0 },
  ]),
  workspace_finpulse: mkUrls([
    { url: 'https://www.bloomberg.com/markets',                                  title: 'Bloomberg Markets — Real-Time Data',           domain: 'bloomberg.com',      domain_type: 'Editorial', retrieved: 34.1, retrieval_rate: 0.6, citation_rate: 2.1, mentions: 3 },
    { url: 'https://www.reuters.com/finance',                                    title: 'Reuters Finance News & Analysis',              domain: 'reuters.com',        domain_type: 'Editorial', retrieved: 28.7, retrieval_rate: 0.5, citation_rate: 1.9, mentions: 2 },
    { url: 'https://www.ft.com/markets',                                         title: 'Financial Times — Markets',                    domain: 'ft.com',             domain_type: 'Editorial', retrieved: 22.3, retrieval_rate: 0.4, citation_rate: 1.7, mentions: 1 },
    { url: 'https://www.wsj.com/finance',                                        title: 'Wall Street Journal — Finance',                domain: 'wsj.com',            domain_type: 'Editorial', retrieved: 19.8, retrieval_rate: 0.3, citation_rate: 1.5, mentions: 1 },
    { url: 'https://seekingalpha.com/market-news',                               title: 'Seeking Alpha — Latest Market News',           domain: 'seekingalpha.com',   domain_type: 'UGC',       retrieved: 15.4, retrieval_rate: 0.3, citation_rate: 1.2, mentions: 0 },
    { url: 'https://www.morningstar.com/stocks',                                 title: 'Morningstar — Stock Research',                 domain: 'morningstar.com',    domain_type: 'Corporate', retrieved: 11.2, retrieval_rate: 0.2, citation_rate: 1.0, mentions: 0 },
  ]),
  workspace_cryptoedge: mkUrls([
    { url: 'https://www.coindesk.com/learn/what-is-bitcoin',                     title: 'What Is Bitcoin? A Guide for Beginners',       domain: 'coindesk.com',       domain_type: 'Editorial', retrieved: 28.4, retrieval_rate: 0.5, citation_rate: 2.3, mentions: 2 },
    { url: 'https://cointelegraph.com/learn/best-crypto-exchanges',              title: 'Best Crypto Exchanges 2026',                   domain: 'cointelegraph.com',  domain_type: 'Editorial', retrieved: 24.6, retrieval_rate: 0.4, citation_rate: 2.1, mentions: 2 },
    { url: 'https://decrypt.co/resources/best-defi-platforms',                  title: 'Best DeFi Platforms for Yield Farming',        domain: 'decrypt.co',         domain_type: 'Editorial', retrieved: 19.2, retrieval_rate: 0.3, citation_rate: 1.8, mentions: 1 },
    { url: 'https://www.theblock.co/data/crypto-markets',                        title: 'Crypto Market Data — The Block',               domain: 'theblock.co',        domain_type: 'Reference', retrieved: 16.7, retrieval_rate: 0.3, citation_rate: 1.4, mentions: 0 },
    { url: 'https://messari.io/screener/crypto',                                 title: 'Crypto Asset Screener — Messari',              domain: 'messari.io',         domain_type: 'Corporate', retrieved: 13.5, retrieval_rate: 0.2, citation_rate: 1.1, mentions: 0 },
    { url: 'https://www.reddit.com/r/CryptoCurrency/top',                        title: 'Top Crypto Discussions This Week',             domain: 'reddit.com',         domain_type: 'UGC',       retrieved: 10.1, retrieval_rate: 0.2, citation_rate: 1.6, mentions: 0 },
  ]),
  workspace_marketlens: mkUrls([
    { url: 'https://tradingeconomics.com/indicators',                            title: 'Global Economic Indicators Dashboard',         domain: 'tradingeconomics.com', domain_type: 'Reference', retrieved: 41.2, retrieval_rate: 0.7, citation_rate: 2.8, mentions: 5 },
    { url: 'https://data.worldbank.org/indicator',                               title: 'World Bank Open Data — Key Indicators',       domain: 'worldbank.org',      domain_type: 'Reference', retrieved: 35.8, retrieval_rate: 0.6, citation_rate: 2.5, mentions: 4 },
    { url: 'https://www.imf.org/en/Publications/WEO',                            title: 'IMF World Economic Outlook 2026',              domain: 'imf.org',            domain_type: 'Reference', retrieved: 29.3, retrieval_rate: 0.5, citation_rate: 2.2, mentions: 3 },
    { url: 'https://www.statista.com/statistics/global-markets',                 title: 'Global Markets Statistics — Statista',         domain: 'statista.com',       domain_type: 'Corporate', retrieved: 22.1, retrieval_rate: 0.4, citation_rate: 1.9, mentions: 2 },
    { url: 'https://www.visualcapitalist.com/category/markets',                  title: 'Markets Visualized — Visual Capitalist',       domain: 'visualcapitalist.com', domain_type: 'Editorial', retrieved: 17.6, retrieval_rate: 0.3, citation_rate: 1.6, mentions: 1 },
    { url: 'https://www.oecd.org/economic-outlook',                              title: 'OECD Economic Outlook Report',                 domain: 'oecd.org',           domain_type: 'Reference', retrieved: 14.0, retrieval_rate: 0.2, citation_rate: 1.3, mentions: 1 },
  ]),
};
